/**
 * Pure-TypeScript Code 128 barcode SVG generator.
 * Supports Code 128B (ASCII 32–127) with auto-switch to Code 128C
 * for runs of 6+ even-length digit sequences.
 */

const CODE_B_START = 104;
const CODE_C_START = 105;
const SWITCH_TO_C = 99;
const SWITCH_TO_B = 100;
const STOP = 106;

const PATTERNS: number[] = [
  0b11011001100, 0b11001101100, 0b11001100110, 0b10010011000,
  0b10010001100, 0b10001001100, 0b10011001000, 0b10011000100,
  0b10001100100, 0b11001001000, 0b11001000100, 0b11000100100,
  0b10110011100, 0b10011011100, 0b10011001110, 0b10111001100,
  0b10011101100, 0b10011100110, 0b11001110010, 0b11001011100,
  0b11001001110, 0b11011100100, 0b11001110100, 0b11101101110,
  0b11101001100, 0b11100101100, 0b11100100110, 0b11101100100,
  0b11100110100, 0b11100110010, 0b11011011000, 0b11011000110,
  0b11000110110, 0b10100011000, 0b10001011000, 0b10001000110,
  0b10110001000, 0b10001101000, 0b10001100010, 0b11010001000,
  0b11000101000, 0b11000100010, 0b10110111000, 0b10110001110,
  0b10001101110, 0b10111011000, 0b10111000110, 0b10001110110,
  0b11101110110, 0b11010001110, 0b11000101110, 0b11011101000,
  0b11011100010, 0b11011101110, 0b11101011000, 0b11101000110,
  0b11100010110, 0b11101101000, 0b11101100010, 0b11100011010,
  0b11101111010, 0b11001000010, 0b11110001010, 0b10100110000,
  0b10100001100, 0b10010110000, 0b10010000110, 0b10000101100,
  0b10000100110, 0b10110010000, 0b10110000100, 0b10011010000,
  0b10011000010, 0b10000110100, 0b10000110010, 0b11000010010,
  0b11001010000, 0b11110111010, 0b11000010100, 0b10001111010,
  0b10100111100, 0b10010111100, 0b10010011110, 0b10111100100,
  0b10011110100, 0b10011110010, 0b11110100100, 0b11110010100,
  0b11110010010, 0b11011011110, 0b11011110110, 0b11110110110,
  0b10101111000, 0b10100011110, 0b10001011110, 0b10111101000,
  0b10111100010, 0b11110101000, 0b11110100010, 0b10111011110,
  0b10111101110, 0b11101011110, 0b11110101110,
  0b11010000100, 0b11010010000, 0b11010011100,
  0b1100011101011,
];

interface EncodedSymbol {
  value: number;
}

function encode(text: string): EncodedSymbol[] {
  const symbols: EncodedSymbol[] = [];
  let i = 0;
  let mode: 'B' | 'C' = 'B';

  const digitRunLength = (pos: number): number => {
    let len = 0;
    while (pos + len < text.length && text.charCodeAt(pos + len) >= 48 && text.charCodeAt(pos + len) <= 57) {
      len++;
    }
    return len;
  };

  const drl = digitRunLength(0);
  if (drl >= 4 && drl % 2 === 0) {
    symbols.push({ value: CODE_C_START });
    mode = 'C';
  } else {
    symbols.push({ value: CODE_B_START });
    mode = 'B';
  }

  while (i < text.length) {
    if (mode === 'B') {
      const run = digitRunLength(i);
      if (run >= 6 && run % 2 === 0) {
        symbols.push({ value: SWITCH_TO_C });
        mode = 'C';
        continue;
      }
      const code = text.charCodeAt(i) - 32;
      symbols.push({ value: code });
      i++;
    } else {
      const run = digitRunLength(i);
      if (run >= 2) {
        const pair = parseInt(text.substring(i, i + 2), 10);
        symbols.push({ value: pair });
        i += 2;
      } else {
        symbols.push({ value: SWITCH_TO_B });
        mode = 'B';
      }
    }
  }

  let checksum = symbols[0].value;
  for (let s = 1; s < symbols.length; s++) {
    checksum += symbols[s].value * s;
  }
  symbols.push({ value: checksum % 103 });
  symbols.push({ value: STOP });
  return symbols;
}

/**
 * Generate an SVG string for a Code 128 barcode.
 * @param text   ASCII string to encode
 * @param height Bar height in pixels (default 50)
 * @returns      SVG markup string (no XML declaration)
 */
export function generateCode128Svg(text: string, height = 50): string {
  if (!text) {
    return '';
  }
  const symbols = encode(text);
  const bits: number[] = [];
  for (const sym of symbols) {
    const pattern = PATTERNS[sym.value];
    const bitCount = sym.value === STOP ? 13 : 11;
    for (let b = bitCount - 1; b >= 0; b--) {
      bits.push((pattern >> b) & 1);
    }
  }

  const moduleWidth = 1;
  const quietZone = 10;
  const totalWidth = quietZone * 2 + bits.length * moduleWidth;

  const rects: string[] = [];
  let x = quietZone;
  for (const bit of bits) {
    if (bit === 1) {
      rects.push(`<rect x="${x}" y="0" width="${moduleWidth}" height="${height}"/>`);
    }
    x += moduleWidth;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalWidth} ${height}" preserveAspectRatio="none" width="100%" height="${height}">${rects.join('')}</svg>`;
}
